import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { COUNTRY_LANGUAGES_CODES } from '@/utils/constants/globalConstants'
import { getDisplayContentLanguagesService } from '@/services/languageService'
import { setToastNotifications } from '@/store/slices/commonSlice'
import { Constants } from '@/utils/constants/commonConstants'
import { convertImageToWebP } from '@/helpers/shadcn/utils'
import { getFileVerification } from '@/services/fileUploadService'
import { ALERT_MIN, ALERT_MAX, IMAGE_SLOTS } from '@/utils/constants/rewardsDisplayConstants'
import { createEmptyImages, clampAlertDisplayCount, getProductCompletion, ensureProductDisplayDefaults } from '@/utils/rewardsDisplay'
import { REWARD_TYPES } from '@/utils/constants/rewardsConstants'
import { getProductList } from '@/services/ProductsService/product'
import { renderPreviewContent } from '@/utils/rewardsDisplay'
import { capitalizeFirstLetter } from '@/utils/commons'

export const useRewardsDisplay = ({
  dispatch,
  formData,
  handleFormChange,
  countryCode,
  displayContentLanguages,
  currentStep,
}) => {
  // Languages – derive from country or fetch fallback
  const [localLanguages, setLocalLanguages] = useState([])

  useEffect(() => {
    const countryLangs = COUNTRY_LANGUAGES_CODES[countryCode]
    if (countryLangs && countryLangs.length > 0) {
      setLocalLanguages(countryLangs)
      return
    }
    if (displayContentLanguages && displayContentLanguages.length > 0) {
      setLocalLanguages(displayContentLanguages)
      return
    }
    let isMounted = true
    getDisplayContentLanguagesService().then(res => {
      if (isMounted) setLocalLanguages(res || [])
    })
    return () => {
      isMounted = false
    }
  }, [countryCode, displayContentLanguages])

  // ---------- Default product images integration ----------
  const extractImageUrls = (productItem = {}) => {
    // Try common shapes/keys to derive up to 3 image URLs
    const candidates = []
    const pushIfUrl = v => {
      if (typeof v === 'string' && /^https?:\/\//i.test(v)) candidates.push(v)
    }

    // Direct keys
    pushIfUrl(productItem.imageUrl)
    pushIfUrl(productItem.imageURL)
    pushIfUrl(productItem.defaultImageUrl)
    pushIfUrl(productItem.defaultImage)
    pushIfUrl(productItem.thumbnailUrl)
    pushIfUrl(productItem.image)

    // Nested shapes
    if (productItem.media && Array.isArray(productItem.media)) {
      productItem.media.forEach(m => pushIfUrl(m?.cdnUrl || m?.url))
    }
    if (Array.isArray(productItem.images)) {
      productItem.images.forEach(im => pushIfUrl(im?.cdnUrl || im?.url || im))
    }
    // Explicit array of URLs as per product API shape
    if (Array.isArray(productItem.imageUrls)) {
      productItem.imageUrls.forEach(u => pushIfUrl(u))
    }

    // De-duplicate and limit to 3
    return Array.from(candidates).slice(0, 3)
  }

  // Reward Type
  const rewardType = useMemo(() => {
    if (formData?.rewardType) {
      return formData?.rewardType
    }
    return formData?.rewardType || ''
  }, [formData?.rewardType])

  // For Cash, Pep Voucher Product, Discount, Physical, Visa Card, Digital, Digital Pre-order, Loyalty, PepVoucher Discount - show single image upload
   const displaySingleImageUpload = useMemo(() => {
     if(formData?.rewardType){
        const list = [
          REWARD_TYPES.CASH, 
          REWARD_TYPES.PEP_VOUCHER_PRODUCT, 
          REWARD_TYPES.DISCOUNT, 
          REWARD_TYPES.PHYSICAL, 
          REWARD_TYPES.VISA_CARD,
          REWARD_TYPES.DIGITAL,
          REWARD_TYPES.DIGITAL_PRE_ORDER,
          REWARD_TYPES.LOYALTY,
          REWARD_TYPES.PEP_VOUCHER_DISCOUNT
        ];
        return list.includes(formData?.rewardType)
     }
     return false
   }, [formData?.rewardType])

  const languages = useMemo(() => {
    return (localLanguages || []).map(l => ({
      code: l.code,
      language: l.language || l.title,
    }))
  }, [localLanguages])

  // Reward description for header
  const rewardDescription = useMemo(() => {
    const preview = renderPreviewContent({ formData, countryCode })
    return preview || formData?.name || ''
  }, [formData, countryCode])

  // Fallback description for EN defaults (do not use reward name here)
  const enFallbackDescription = useMemo(() => {
    const preview = renderPreviewContent({ formData, countryCode })
    return preview || ''
  }, [formData, countryCode])

  // Defensive default for display state
  const display = useMemo(
    () => formData?.display || { productDisplay: {} },
    [formData?.display]
  )

  // Effective products from setup
  const effectiveProducts = useMemo(() => {
    const selected = formData?.selectedProducts || []
    return selected.length > 0 ? selected : []
  }, [formData?.selectedProducts])

  // Dropdown options from selected products
  const productOptions = useMemo(() => {
    return (effectiveProducts || []).map(p => {
      const id = p.value
      const pd = display?.productDisplay?.[id]
      const { completed } = getProductCompletion(pd, languages, formData?.rewardType)
      return {
        id,
        displayText: p.label,
        isBadge: false,
        completed,
      }
    })
  }, [effectiveProducts, display?.productDisplay, languages, formData?.rewardType])

  // Child list for atoms Dropdown
  const productChildList = useMemo(
    () =>
      productOptions.map(o => ({
        id: o.id,
        displayText: o.displayText,
        leadingIcon: { icon: o.completed ? 'check_circle' : 'circle' },
      })),
    [productOptions]
  )

  // Active product id used in display step
  const [activeProductId, setActiveProductId] = useState(effectiveProducts?.[0]?.value)

  // Local toast state for RewardDisplay ToasterAlert
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')

  useEffect(() => {
    if (!activeProductId && effectiveProducts?.length > 0) {
      setActiveProductId(effectiveProducts[0].value)
    }
  }, [effectiveProducts, activeProductId])

  // Completion helper for header counter
  const completedCount = useMemo(() => {
    const ids = (effectiveProducts || []).map(p => p.value)
    let count = 0
    ids.forEach(id => {
      const pd = display?.productDisplay?.[id]
      const { completed } = getProductCompletion(pd, languages, formData?.rewardType)
      if (completed) count += 1
    })
    return { count, total: ids.length }
  }, [effectiveProducts, display, languages, formData?.rewardType])

  // Helper to update productDisplay entry for active product
  const updateDisplayForId = useCallback(
    (productId, update) => {
      const current = display.productDisplay[productId] || {}
      const nextEntry = { ...current, ...update }
      const same =
        Object.keys(nextEntry).length === Object.keys(current).length &&
        Object.keys(nextEntry).every(k => nextEntry[k] === current[k])
      if (same) return
      const nextDisplay = {
        ...display,
        productDisplay: {
          ...display.productDisplay,
          [productId]: nextEntry,
        },
      }
      handleFormChange('display', nextDisplay)
    },
    [display, handleFormChange]
  )

  const updateProductDisplay = useCallback(
    (update) => {
      if (!activeProductId) return
      updateDisplayForId(activeProductId, update)
    },
    [activeProductId, updateDisplayForId]
  )

  const updateProductDisplayFor = useCallback(
    (productId, update) => {
      updateDisplayForId(productId, update)
    },
    [updateDisplayForId]
  )

  // Build product completion warning message
  const buildCompletionWarningMessage = useCallback(
    (displayState, selected, lngs) => {
      const status = selected.map(p => {
        const id = p.value
        const pd = displayState?.productDisplay?.[id]
        const { imageOk, textOk, alertOk } = getProductCompletion(pd, lngs, formData?.rewardType)
        return { label: p.label, imageOk, textOk, alertOk }
      })
      const anyMissingImage = status.some(s => !s.imageOk)
      const anyMissingAlert = formData?.rewardType === REWARD_TYPES.BONUS_PRODUCTS ? status.some(s => !s.alertOk) : false
      const anyMissingText = status.some(s => !s.textOk)
      const productsWithMissing = status.filter(s => !s.imageOk || !s.alertOk || !s.textOk)
      if (productsWithMissing.length === 0) return null
      const parts = []
      if (anyMissingImage) parts.push('Image(s)')
      if (anyMissingAlert) parts.push('Alert display count')
      if (anyMissingText) parts.push('Title and Description for all languages')
      const productList = productsWithMissing.map(s => s.label).join(', ')
      return `${parts.join(', ')} must be specified for all selected products.\n${productList}`
    },
    [formData?.rewardType]
  )

  // Shared toast notification helper
  const showToastNotification = useCallback((message, type = 'error') => {
    dispatch(
      setToastNotifications({
        show: true,
        message,
        type,
      })
    )
  }, [dispatch])

  // Ensure EN defaults for a product
  const ensureEnglishDefaults = useCallback(
  (productId) => {
    const pd = display?.productDisplay?.[productId] || {}
    const suppressed = !!pd.enAutofillDisabled
    if (suppressed) return

    const currentText = pd.rewardText || {}
    const currentEn = currentText.en || {}
    const hasEnTitle = typeof currentEn.title === 'string' && currentEn.title.trim().length > 0
    const hasEnDesc = typeof currentEn.description === 'string' && currentEn.description.trim().length > 0

    const selected = effectiveProducts || []
    const product = selected.find(p => p.value === productId)
    const defaultTitle = product?.label || ''
    const defaultDescription = enFallbackDescription

    const nextEn = { ...currentEn }
    let changed = false

    if (!hasEnTitle && defaultTitle) {
      nextEn.title = defaultTitle
      changed = true
    }

    if (!hasEnDesc && defaultDescription) {
      nextEn.description = defaultDescription
      changed = true
    }

    // If we won't change anything, bail out to avoid an update loop
    if (!changed) return

    const nextRewardText = { ...currentText, en: nextEn }
    updateProductDisplayFor(productId, { rewardText: nextRewardText })
  },
  [display?.productDisplay, effectiveProducts, enFallbackDescription, updateProductDisplayFor]
)

  // Fetch product default images and set into display for the given product
  const fetchInFlightRef = useRef(new Set())
  const fetchAndSetDefaultImages = useCallback(async (productId) => {
    if (fetchInFlightRef.current.has(productId)) return
    fetchInFlightRef.current.add(productId)
    try {
      const qp = new URLSearchParams()
      qp.append('pageSize', 1)
      qp.append('page', 0)
      const filters = [
        { field: 'productId', type: 'string', value: String(productId) },
      ]
      const resp = await getProductList(qp.toString(), filters)
      const data = resp?.data ?? []
      const items = Array.isArray(data) ? data : (data?.items ?? [])
      const item = items?.[0] || null
      if (!item) return
      const urls = extractImageUrls(item)
      if (!urls || urls.length === 0) {
        showToastNotification('Default images are not available for this product.', 'warning')
        return
      }
      const mapped = [
        { position: 1, url: urls[0] || '', file: null },
        { position: 2, url: urls[1] || '', file: null },
        { position: 3, url: urls[2] || '', file: null },
      ]
      // Persist toggle state to true alongside images to avoid any unintended reset
      updateProductDisplayFor(productId, { images: mapped, useDefaultImage: true })
    } catch (error) {
      showToastNotification('Failed to fetch default product images.')
    } finally {
      fetchInFlightRef.current.delete(productId)
    }
  }, [updateProductDisplayFor, showToastNotification])

  // Initialize productDisplay entry for active product and ensure English defaults
  useEffect(() => {
    if (currentStep !== 2) return
    if (!activeProductId) return
    const pd = display?.productDisplay?.[activeProductId]
    if (!pd) {
      const ensured = ensureProductDisplayDefaults(activeProductId, display, effectiveProducts, undefined, enFallbackDescription)
      const currentEnsured = display?.productDisplay?.[activeProductId]
      const isSame = currentEnsured && JSON.stringify(currentEnsured) === JSON.stringify(ensured)
      if (!isSame) {
        handleFormChange('display', {
          ...display,
          productDisplay: {
            ...display.productDisplay,
            [activeProductId]: ensured,
          },
        })
      }
      return
    }
    // Ensure EN defaults if missing
    ensureEnglishDefaults(activeProductId)
  }, [currentStep, activeProductId, display, handleFormChange, effectiveProducts, updateProductDisplay, enFallbackDescription, ensureEnglishDefaults])

  // Toggle default image
  const useDefaultImageToggleValue = !!display?.productDisplay?.[activeProductId]?.useDefaultImage
  const handleUseDefaultImageToggleChange = value => {
    const current = display?.productDisplay?.[activeProductId] || {}
    if (value) {
      // Backup current custom images once
      if (!current.customImagesBackup) {
        const backup = Array.isArray(current.images) ? current.images.map(i => ({ ...i })) : createEmptyImages()
        updateProductDisplay({ useDefaultImage: true, customImagesBackup: backup })
      } else {
        updateProductDisplay({ useDefaultImage: true })
      }
      if (activeProductId) {
        // Fetch default images for the active product
        fetchAndSetDefaultImages(activeProductId)
      }
    } else {
      // Clear all images to allow fresh uploads when turning off default images
      updateProductDisplay({ useDefaultImage: false, images: createEmptyImages(), customImagesBackup: undefined })
    }
  }

  // Auto-fetch defaults when switching/initializing if toggle is on and images are empty
  useEffect(() => {
    if (currentStep !== 2) return
    if (!activeProductId) return
    if (!useDefaultImageToggleValue) return
    const current = display?.productDisplay?.[activeProductId] || {}
    const imgs = current.images || []
    const hasAny = Array.isArray(imgs) && imgs.some(i => i?.url)
    if (!hasAny) {
      fetchAndSetDefaultImages(activeProductId)
    }
  }, [currentStep, activeProductId, useDefaultImageToggleValue, display?.productDisplay, fetchAndSetDefaultImages])

  // Alert display count (sanitized for UI)
  const alertDisplayCount = (() => {
    const raw = display?.productDisplay?.[activeProductId]?.alertDisplayCount
    let num = Number(raw)
    if (!Number.isFinite(num)) num = 0
    return Math.max(ALERT_MIN, Math.min(ALERT_MAX, num))
  })()
  const setAlertDisplayCount = val => {
    if (val === '' || val === null || val === undefined) {
      updateProductDisplay({ alertDisplayCount: '' })
      return
    }
    const num = clampAlertDisplayCount(val, ALERT_MIN, ALERT_MAX)
    updateProductDisplay({ alertDisplayCount: num })
  }

  // Shown on homepage per product
  const shownOnHomepage = !!display?.productDisplay?.[activeProductId]?.shownOnHomepage
  const handleShownOnHomePageChange = value => {
    updateProductDisplay({ shownOnHomepage: value })
  }

  // Images helper for active product (sorted)
  const imagesForActiveProduct = useMemo(() => {
    const current = display?.productDisplay?.[activeProductId] || {}
    return (current?.images || createEmptyImages())
      .slice()
      .sort((a, b) => Number(a?.position) - Number(b?.position))
  }, [display?.productDisplay, activeProductId])

  // Shared image upload helper
  const uploadImageToService = useCallback(async (file) => {
    const converted = await convertImageToWebP(file, { entityType: 'rewards' })
    const webpFile = converted.webpFile
    const metadata = converted.metadata

    const metadataPayload = {
      mediaUploadMetadata: [
        {
          id: metadata.id,
          referenceEntity: metadata.referenceEntity,
          originalFileName: metadata.originalFileName,
          originalFormat: metadata.originalFormat,
          originalSize: metadata.originalSize,
          width: metadata.width,
          height: metadata.height,
          blobFileSize: metadata.blobFileSize,
          blobFormat: metadata.blobFormat,
        },
      ],
    }

    const form = new FormData()
    form.append('images', webpFile)
    form.append('metadata', JSON.stringify(metadataPayload))

    const response = await getFileVerification(
      form,
      Constants.Media,
      undefined, // onUploadProgress
      'webp'
    )

    return response?.[0]?.media?.cdnUrl
  }, [])

  // Shared error handler for image upload failures
  const handleImageUploadError = useCallback(() => {
    showToastNotification('Image upload failed. Please try again.')
  }, [showToastNotification])

  // Image select/delete handler for a given position
  const onImageSelectAt = useCallback((position) => async (file) => {
    if (useDefaultImageToggleValue) return
    const images = imagesForActiveProduct.slice()
    // Delete: compact left and clear last slot
    if (!file) {
      const idx = Math.max(0, Math.min(IMAGE_SLOTS - 1, position - 1))
      const kept = images.filter((_, i) => i !== idx)
      const shifted = kept.map((img, i) => ({ ...img, position: i + 1 }))
      const result = [
        shifted[0] || { position: 1, url: '', file: null },
        shifted[1] || { position: 2, url: '', file: null },
        shifted[2] || { position: 3, url: '', file: null },
      ]
      updateProductDisplay({ images: result })
      return
    }
    // Optimistic preview
    let previewUrl
    try {
      previewUrl = URL.createObjectURL(file)
      const withPreview = images.map(img =>
        img.position === position ? { position, url: previewUrl, file } : img
      )
      updateProductDisplay({ images: withPreview })

      // Upload flow using shared helper
      const cdnUrl = await uploadImageToService(file)
      if (cdnUrl) {
        const finalized = withPreview.map(img =>
          img.position === position ? { position, url: cdnUrl, file: null } : img
        )
        updateProductDisplay({ images: finalized })
      }
    } catch (error) {
      handleImageUploadError(error)
      // Rollback preview
      const rolledBack = images.map(img =>
        img.position === position ? { position, url: '', file: null } : img
      )
      updateProductDisplay({ images: rolledBack })
    } finally {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [useDefaultImageToggleValue, imagesForActiveProduct, updateProductDisplay, uploadImageToService, handleImageUploadError])

  // Product switch with warning toast if current incomplete
  const onProductChange = useCallback((value) => {
    const nextId = value?.[0]?.id
    const current = display.productDisplay[activeProductId]
    if (activeProductId && current) {
      const selected = formData?.selectedProducts || []
      const message = buildCompletionWarningMessage(display, selected, languages)
      if (message) {
        setToastMessage(message)
        setShowToast(true)
      }
    }
    setActiveProductId(nextId)
    // After switching, set defaults for English on the targeted product if not already present
    if (nextId) {
      ensureEnglishDefaults(nextId)
    }
  }, [display, activeProductId, formData?.selectedProducts, languages, buildCompletionWarningMessage, ensureEnglishDefaults])

  const imageUpload = useCallback(() => async (file) => {
      // Optimistic preview
      let previewUrl
      try {
        previewUrl = URL.createObjectURL(file)
        handleFormChange("imageUrls", [previewUrl])
  
        // Upload flow using shared helper
        const cdnUrl = await uploadImageToService(file)
        if (cdnUrl) {
          handleFormChange("imageUrls", [cdnUrl])
        }
      } catch (error) {
        // Rollback preview
        handleFormChange("imageUrls", [])
      } finally {
        if (previewUrl) URL.revokeObjectURL(previewUrl)
      }
    }, [handleFormChange, uploadImageToService])

  // Update global reward text helper - applies title and description for a specific language
  const updateGlobalRewardText = useCallback((language, patch) => {
    const currentText = formData.rewardText || {}

    // Build the updated reward text object
    const nextRewardText = {
      ...currentText,
      [language]: {
        ...currentText[language],
        ...patch
      }
    }
    
    // Update the global reward text in formData
    handleFormChange('rewardText', nextRewardText)
  }, [formData.rewardText, handleFormChange])

  // Update reward text helper
  const updateRewardText = useCallback((language, patch) => {
    const current = display.productDisplay[activeProductId] || {}
    const currentText = current.rewardText || {}
    const nextRewardText = { ...currentText, [language]: { ...currentText?.[language], ...patch } }
    const shouldDisableEnAutofill =
      language === 'en' &&
      ((Object.prototype.hasOwnProperty.call(patch, 'title') && patch.title === '') ||
        (Object.prototype.hasOwnProperty.call(patch, 'description') && patch.description === ''))
    updateProductDisplay({
      rewardText: nextRewardText,
      ...(shouldDisableEnAutofill ? { enAutofillDisabled: true } : {}),
    })
  }, [display.productDisplay, activeProductId, updateProductDisplay])

  // Update description for single image upload types
  useEffect(() => {
    if (currentStep == 2 && formData?.rewardType && rewardDescription && displaySingleImageUpload && !formData.rewardText) {
      updateGlobalRewardText('en', { description: rewardDescription, title: capitalizeFirstLetter(formData?.rewardType?.toLowerCase()) })
    }
  }, [currentStep, formData?.rewardType, rewardDescription, displaySingleImageUpload, formData.rewardText, updateGlobalRewardText])

  // Update title for PEP voucher products
  useEffect(() => {
    if(rewardType && rewardDescription && rewardType === REWARD_TYPES?.PEP_VOUCHER_PRODUCT && displaySingleImageUpload && formData?.selectedPepVouchersProducts?.[0]?.label && currentStep == 2 && !formData?.rewardText){
      const selectedProductLabel = formData?.selectedPepVouchersProducts?.[0]?.label
      updateGlobalRewardText('en', { description: rewardDescription, title: selectedProductLabel })
    }
  }, [currentStep, rewardType, rewardDescription, displaySingleImageUpload, formData?.selectedPepVouchersProducts, formData?.rewardText, updateGlobalRewardText])

  return {
    imageUpload,
    updateGlobalRewardText,
    rewardType,
    displaySingleImageUpload,
    languages,
    rewardDescription,
    display,
    effectiveProducts,
    productOptions,
    productChildList,
    completedCount,
    activeProductId,
    setActiveProductId,
    updateProductDisplay,
    useDefaultImageToggleValue,
    handleUseDefaultImageToggleChange,
    alertDisplayCount,
    setAlertDisplayCount,
    shownOnHomepage,
    handleShownOnHomePageChange,
    imagesForActiveProduct,
    onImageSelectAt,
    onProductChange,
    updateRewardText,
    showToast,
    setShowToast,
    toastMessage,
  }
}
